/**
 * Grouping the fleet by what firmware it is running.
 *
 * A Ruijie fleet is never on one version. Measured 2026-08-23: 165 hybrid
 * routers spread across six versions, 131 of them on 2.0.3 and 4 on a 2.1.2
 * canary. "Which boxes are ahead of the fleet" and "which are still on the old
 * line" are the two questions asked before every rollout, and neither could be
 * answered from the list.
 */

/** Strip the CI suffix: "2.1.4-0c5cd56-staging" → "2.1.4". */
export const fwBase = (v?: string | null): string => (v ?? '').split('-')[0];

/** The line a version belongs to: "2.1.4" → "2.1.x". */
export const fwLine = (v?: string | null): string => {
   const parts = fwBase(v).split('.');
   return parts.length >= 2 ? `${parts[0]}.${parts[1]}.x` : '';
};

/** Compare two base versions numerically. Missing parts count as 0. */
export const cmpFw = (a: string, b: string): number => {
   const pa = fwBase(a).split('.').map((n) => parseInt(n, 10) || 0);
   const pb = fwBase(b).split('.').map((n) => parseInt(n, 10) || 0);
   for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
      const d = (pa[i] ?? 0) - (pb[i] ?? 0);
      if (d) return d;
   }
   return 0;
};

export interface FleetVersionGroups {
   /** Distinct lines present, newest first: ["2.1.x", "2.0.x", "1.0.x"]. */
   lines: string[];
   /** The version the BULK of the fleet runs. */
   majority: string;
   /**
    * The newest version present, when it is ahead of the majority — the canary.
    *
    * ⚠️ Only the NEWEST one (Ray, 2026-08-23: "the one latest being canaried,
    * don't include the older version being canaried"). With 131 boxes on 2.0.3,
    * 4 on 2.1.2 and 2 on 2.1.4, the canary is 2.1.4 alone. Counting everything
    * ahead of the fleet would keep showing last week's canary forever, which is
    * the opposite of what the filter is for.
    *
    * Empty when nothing is ahead — a fleet that is all on one version has no
    * canary, and an empty chip is better than one that silently means nothing.
    */
   canary: string;
}

export const groupFleetVersions = (versions: (string | null | undefined)[]): FleetVersionGroups => {
   const bases = versions.map(fwBase).filter(Boolean);
   if (!bases.length) return { lines: [], majority: '', canary: '' };

   const counts = new Map<string, number>();
   bases.forEach((b) => counts.set(b, (counts.get(b) ?? 0) + 1));

   let majority = '';
   let best = -1;
   counts.forEach((n, v) => {
      // Ties break toward the OLDER version: the bulk of a fleet is what it has
      // not moved off yet, not what it is moving to.
      if (n > best || (n === best && cmpFw(v, majority) < 0)) {
         best = n;
         majority = v;
      }
   });

   const newest = bases.reduce((a, b) => (cmpFw(b, a) > 0 ? b : a), bases[0]);
   const canary = cmpFw(newest, majority) > 0 ? newest : '';

   const lines = Array.from(new Set(bases.map(fwLine).filter(Boolean))).sort(
      (a, b) => cmpFw(b.replace('.x', '.0'), a.replace('.x', '.0'))
   );

   return { lines, majority, canary };
};

/**
 * The same grouping, but kept SEPARATE per product line.
 *
 * ⚠️ Cloud and hybrid are different fleets on different number lines — cloud
 * runs 1.x and hybrid runs 2.x. Pooling them makes "the newest version" mean the
 * newest hybrid one, so every cloud box reads as behind, and the canary chip
 * points at a version no cloud device could ever run (Ray, 2026-08-23: "make
 * sure filter it also with cloud and hybrid dont mix").
 *
 * A device is judged against ITS OWN line, whatever the variant filter is set to.
 */
export interface FleetVersionsByVariant {
   /** Union of the lines present across both, newest first — for the chips. */
   lines: string[];
   /** The canary version for each variant, keyed by variant. '' = none ahead. */
   canaryFor: Record<string, string>;
   majorityFor: Record<string, string>;
}

export const groupFleetVersionsByVariant = (
   rows: { version?: string | null; variant?: string | null }[]
): FleetVersionsByVariant => {
   const byVariant = new Map<string, (string | null | undefined)[]>();
   rows.forEach((r) => {
      const v = r.variant === 'hybrid' ? 'hybrid' : 'cloud';
      if (!byVariant.has(v)) byVariant.set(v, []);
      byVariant.get(v)!.push(r.version);
   });

   const canaryFor: Record<string, string> = {};
   const majorityFor: Record<string, string> = {};
   const lineSet = new Set<string>();
   byVariant.forEach((versions, variant) => {
      const g = groupFleetVersions(versions);
      canaryFor[variant] = g.canary;
      majorityFor[variant] = g.majority;
      g.lines.forEach((l) => lineSet.add(l));
   });

   const lines = Array.from(lineSet).sort((a, b) =>
      cmpFw(b.replace('.x', '.0'), a.replace('.x', '.0'))
   );
   return { lines, canaryFor, majorityFor };
};

/**
 * The router firmware where the box first learned to change its own internet
 * settings (`smartfi-set-wan`, shipped 2026-08-27).
 *
 * Below this the Internet (WAN) tab can do NOTHING — the CGI is not on the
 * image at all, and the whole cloud line (1.x) never received it. Worse, a box
 * without it answers a captive-portal REDIRECT rather than "not found", so the
 * cloud used to time out and tell the operator their healthy router was
 * offline. Measured on prod 2026-09-04: 240 of 396 Ruijie/OrangePi main vendos
 * were below this and were shown the tab anyway.
 */
export const WAN_MIN_FIRMWARE = '2.3.8';

/** First OpenWrt image whose agent builds VLANs (sub-vendo zones AND the
 *  tagged main zone, vlan.sh 2026-08-02): Ruijie/AX/Comfast 2.6.13, and every
 *  OrangePi / mini PC 2.7.x. Below it the "Customers on a VLAN tag" card is
 *  not offered — the router would ignore the setting. */
export const CUSTOMER_VLAN_MIN_FIRMWARE = '2.6.13';
export const customerVlanCapable = (v?: string | null): boolean =>
   !!fwBase(v) && cmpFw(v ?? '', CUSTOMER_VLAN_MIN_FIRMWARE) >= 0;

/** Can this router's firmware change its own internet settings? An unknown or
 *  unparseable version reads as NO — never offer a control that could take a
 *  shop offline to a router that has not told us what it runs. */
export const wanCapable = (v?: string | null): boolean =>
   !!fwBase(v) && cmpFw(v ?? '', WAN_MIN_FIRMWARE) >= 0;
